import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { describeDays, formatDate, formatDay, formatPeriod, formatRange } from '../../lib/format/dates.ts';
import { weeksAgainstToday } from '../../lib/scheduling/now.ts';
import { physiciansOnCall } from '../../lib/scheduling/people.ts';
import { api, type ScheduleDetail, type WeekView } from '../api.ts';
import { copy } from '../copy/en-CA.ts';
import { DistributionTable } from './DistributionTable.tsx';
import { Notice } from './Notice.tsx';
import { WeekTable } from './WeekTable.tsx';

const c = copy.schedule;

export interface ScheduleViewProps {
  detail: ScheduleDetail;
  /** A link back to where the schedule was opened from, above the title. */
  crumb?: ReactNode;
  /** Rendered after the summary cards, above the warnings and tabs: notices and the admin's draft and edit controls. */
  toolbar?: ReactNode;
  renderControls?: (week: WeekView, index: number) => ReactNode;
  controlsHeader?: string;
  /** Admins only: who asked for what in each week, as the table's last column. */
  renderRequests?: (week: WeekView) => ReactNode;
}

/**
 * The cards under the title. For a physician their next call comes first,
 * since it is what they open the app for, then who is on call now (for
 * everyone), both from every saved schedule; then this schedule's own
 * counts. On a finished schedule or a draft there is no "now" or "next" to
 * speak of, so those two cards are left out rather than shown wrong.
 *
 * There is no twelve-month long-weekend card: on its own it meant nothing
 * to a physician, and it covers more than twelve months. The figure stays
 * in the Distribution tab, where its note explains it.
 */
function Summary({ detail, live }: { detail: ScheduleDetail; live: boolean }) {
  const me = detail.me;
  const now = live ? detail.onCallNow : null;
  const p = copy.personal;
  const s = detail.schedule;
  const nowIsMe = me !== null && now?.physician?.id === me.physicianId;
  if (!now && !me) return null;
  // A week in another saved schedule says which, as a link to it.
  const elsewhere = (scheduleId: string, name: string): ReactNode =>
    scheduleId === s.id ? null : (
      <>
        {' · '}
        {p.inSchedule} <Link to={`/schedules/${scheduleId}`}>{name}</Link>
      </>
    );
  return (
    <section className="personal" aria-label={p.summary}>
      {me && live ? (
        <div className={`personal-card${nowIsMe ? '' : ' accent'}`}>
          <div className="label">{p.next}</div>
          {me.nextWeek ? (
            <>
              <div className="big">{formatRange(me.nextWeek.onCallStart, me.nextWeek.onCallEnd)}</div>
              <div className="sub">
                {describeDays(me.nextWeek)}
                {me.nextWeekSchedule ? elsewhere(me.nextWeekSchedule.id, me.nextWeekSchedule.name) : null}
              </div>
            </>
          ) : (
            <div className="big">{p.none}</div>
          )}
        </div>
      ) : null}
      {now ? (
        <div className={`personal-card${nowIsMe ? ' accent' : ''}`}>
          <div className="label">{p.onCallNow}</div>
          <div className="big">
            {now.physician ? (
              <span className="physician">
                <span className="dot" style={{ background: now.physician.colour }} aria-hidden="true" />
                {nowIsMe ? `${p.you} (${now.physician.name})` : now.physician.name}
              </span>
            ) : (
              p.nobody
            )}
          </div>
          <div className="sub">
            {p.until(formatDay(now.week.onCallEnd))}
            {elsewhere(now.scheduleId, now.scheduleName)}
          </div>
        </div>
      ) : null}
      {me ? (
        <div className="personal-card">
          <div className="label">{p.thisSchedule}</div>
          <div className="big">{p.thisScheduleLine(me.weeks, me.longWeekends)}</div>
          <div className="sub">{p.special(me.specialWeeks)}</div>
        </div>
      ) : null}
    </section>
  );
}

/**
 * Export CSV and Print. Rendered in two places, beside the title on a wide
 * screen and at the end of the page on a phone, and the stylesheet shows
 * exactly one of them (the other is display: none, so it is out of the
 * accessibility tree too). Two places rather than CSS reordering, so that a
 * screen reader and the Tab key meet the buttons where the eye does: on a
 * phone, after the weeks, not before "Your next call".
 */
function ScheduleActions({ scheduleId, placement }: { scheduleId: string; placement: 'head' | 'end' }) {
  return (
    <div className={`row no-print schedule-actions ${placement}`}>
      <a className="btn" href={api.csvUrl(scheduleId)} download>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M12 3v12" />
          <path d="m7 10 5 5 5-5" />
          <path d="M5 21h14" />
        </svg>
        {c.exportCsv}
      </a>
      <button
        type="button"
        className="btn"
        onClick={() => {
          window.print();
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M6 9V3h12v6" />
          <rect x="4" y="9" width="16" height="8" rx="2" />
          <path d="M6 14h12v7H6z" />
        </svg>
        {c.print}
      </button>
    </div>
  );
}

export function ScheduleView({ detail, crumb, toolbar, renderControls, controlsHeader, renderRequests }: ScheduleViewProps) {
  const [tab, setTab] = useState<'weeks' | 'distribution'>('weeks');
  const [showEarlier, setShowEarlier] = useState(false);
  const s = detail.schedule;
  const position = weeksAgainstToday(detail.weeks, detail.today);
  // "Now" and "next" belong on a saved schedule that is still running or to come.
  const live = s.status === 'saved' && !position.finished;
  const hiddenEarlier = showEarlier ? 0 : position.earlier;
  const myId = detail.me?.physicianId ?? null;
  // The people on call in this schedule, not today's roster: a past schedule
  // had the people of its day.
  const onCall = physiciansOnCall(detail.weeks);
  // The server sends the field to admins only; the column follows the data.
  const showLastSpecial = detail.distribution.some((r) => 'lastSpecialWeek' in r);

  return (
    <>
      <div className="schedule-head">
        <div className="page-title">
          {crumb ? <p className="muted small no-print">{crumb}</p> : null}
          <h1>
            {s.name} {s.status === 'draft' ? <span className="badge pending">{c.draftBadge}</span> : null}
          </h1>
          <p>
            {formatPeriod(s.startDate, s.endDate)} · {c.summary(s.weekCount, onCall)}
            {s.savedOn ? ` · ${c.savedOn(formatDate(s.savedOn))}` : ''}
          </p>
        </div>
        <ScheduleActions scheduleId={s.id} placement="head" />
      </div>

      {/* Straight after the title, so on a phone the first screen is the title and the next call. */}
      <Summary detail={detail} live={live} />

      {toolbar}

      {s.unassignedWeeks > 0 ? <Notice kind="error">{c.warnings.unassigned(s.unassignedWeeks)}</Notice> : null}
      {s.conflictWeeks > 0 ? <Notice kind="error">{c.warnings.conflicts(s.conflictWeeks)}</Notice> : null}
      {!s.longWeekendSpreadOk ? <Notice kind="error">{c.warnings.spread}</Notice> : null}

      <div className="tabs no-print" role="tablist">
        <button type="button" role="tab" aria-selected={tab === 'weeks'} className={`tab ${tab === 'weeks' ? 'on' : ''}`} onClick={() => { setTab('weeks'); }}>
          {c.tabs.weeks}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'distribution'}
          className={`tab ${tab === 'distribution' ? 'on' : ''}`}
          onClick={() => { setTab('distribution'); }}
        >
          {c.tabs.distribution}
        </button>
        <div className="grow" />
        <div className="legend">
          <span>
            <span className="cell we mini" aria-hidden="true" /> {c.legend.weekend}
          </span>
          <span>
            <span className="cell hol mini" aria-hidden="true" /> {c.legend.holiday}
          </span>
          <span>
            <span className="cell ghost mini" aria-hidden="true">
              <svg className="x" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round">
                <path d="M4 4l16 16M20 4L4 20" />
              </svg>
            </span>{' '}
            {c.legend.coveredMonday}
          </span>
          <span className="badge lw">{c.kinds['long-weekend']}</span>
          <span className="badge xmas">{c.kinds.christmas}</span>
          <span className="badge ny">{c.kinds['new-year']}</span>
        </div>
      </div>

      <div className={tab === 'weeks' ? 'card table-card' : 'card table-card print-only'}>
        {position.earlier > 0 ? (
          // The weeks already over are folded away so the list opens at this
          // week; they still print, and one press shows them.
          <div className="earlier-toggle no-print">
            <button
              type="button"
              className="btn small"
              aria-expanded={showEarlier}
              onClick={() => {
                setShowEarlier((v) => !v);
              }}
            >
              {showEarlier ? c.hideEarlier : c.showEarlier(position.earlier)}
            </button>
          </div>
        ) : null}
        <WeekTable
          weeks={detail.weeks}
          physicians={detail.physicians}
          myPhysicianId={myId}
          hiddenEarlier={hiddenEarlier}
          nowIndex={position.nowIndex}
          {...(renderControls ? { renderControls } : {})}
          {...(controlsHeader ? { controlsHeader } : {})}
          {...(renderRequests ? { renderRequests } : {})}
        />
      </div>
      <div className={tab === 'distribution' ? '' : 'print-only'}>
        <DistributionTable rows={detail.distribution} physicians={detail.physicians} schedule={s} myPhysicianId={myId} showLastSpecial={showLastSpecial} />
      </div>

      <ScheduleActions scheduleId={s.id} placement="end" />
    </>
  );
}
